<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import {
		Button,
		Text,
		Toggle,
		ColorWheel,
		TemperatureWheel,
		Alert,
		Slider,
	} from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		real_id,
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_device_component_index,
	} from 'src/helpers/interface_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { format } from 'svelte-i18n';

	export let device;

	interface SettingRequestButtonPresets {
		id: number;
		config: {
			button_presets: {
				button_doublepush: {
					key: string;
					enabled: boolean;
					brightness?: number;
					rgb?: number[];
					ct?: number;
				} | null;
			};
		};
	}

	const minBrightness = 1;
	const maxBrightness = 100;
	const supportsRgb = device?.mode === 'rgb';
	const supportsCt = device?.mode === 'cct';

	let settings = readDeviceSettings(device.id);
	let buttonPresets = settings?.[get_gen_2_component_channel_structure(device)]?.button_presets;

	console.log('presets', buttonPresets);

	let loading = false;
	let enabledBrightness =
		buttonPresets?.button_doublepush && buttonPresets?.button_doublepush?.brightness !== null;

	let enabledCtRgb = buttonPresets?.button_doublepush && buttonPresets?.button_doublepush?.ct !== null;

	let cacheBrightnessEnabled = enabledBrightness;
	let cacheCtRgbEnabled = enabledCtRgb;

	let brightness = enabledBrightness ? buttonPresets?.button_doublepush?.brightness : 100; //setting default brightness to 100% because this is correct value for web ui
	let cacheBrightness = brightness;

	const changeColor = (e) => {
		color = e.detail.color;
		rgb = joinColors(color);
	};

	const changeCT = (newCt) => {
		ct = newCt;
	};

	const validateRgb = (_value) => {
		const max = 255;
		const min = 0;

		if (+_value > max) {
			_value = max;
		}
		if (+_value < min) {
			_value = min;
		}
		return +_value;
	};
	function splitColors(color) {
		return {
			r: validateRgb(color[0]),
			g: validateRgb(color[1]),
			b: validateRgb(color[2]),
		};
	}
	function joinColors(color) {
		return [validateRgb(color.r), validateRgb(color.g), validateRgb(color.b)];
	}

	let rgb =
		enabledCtRgb && supportsRgb
			? settings?.[get_gen_2_component_channel_structure(device)]?.button_presets
					?.button_doublepush?.rgb
			: [255, 255, 255];
	let chacheRgb = [...rgb];

	let colorTempRange = settings[`${get_gen_2_component_channel_structure(device)}`]?.ct_range;
	let minCt = supportsCt ? colorTempRange[0] : undefined;
	let maxCt = supportsCt ? colorTempRange[1] : undefined;

	let ct =
		enabledCtRgb && supportsCt
			? settings?.[get_gen_2_component_channel_structure(device)]?.button_presets
					?.button_doublepush?.ct
			: 3000;
	let cacheCt = ct;
	let color = splitColors(rgb);

	const toggleBrightness = (e) => {
		enabledBrightness = e.target.checked;
	};

	const toggleCtRgb = (e) => {
		enabledCtRgb = e.target.checked;
	};

	const sendReq = async () => {
		if (
			cacheBrightness !== brightness ||
			cacheBrightnessEnabled !== enabledBrightness ||
			cacheCtRgbEnabled !== enabledCtRgb ||
			(supportsRgb && chacheRgb !== rgb) ||
			(supportsCt && cacheCt !== ct)
		) {
			loading = true;

			let params: SettingRequestButtonPresets =
				!enabledBrightness && !enabledCtRgb
					? {
							id: get_device_component_index(device),
							config: {
								button_presets: {
									button_doublepush: null,
								},
							},
						}
					: {
							id: get_device_component_index(device),
							config: {
								button_presets: {
									button_doublepush: {
										key: 'button_doublepush',
										enabled: enabledBrightness,
										brightness: enabledBrightness ? Number(brightness) : null,
										rgb: enabledCtRgb ? rgb : null,
										ct: enabledCtRgb ? ct : null,
									},
								},
							},
						};

			// Delete unpropriate keys
			if (enabledCtRgb && !supportsCt) {
				delete params.config.button_presets.button_doublepush.ct;
			}
			if (enabledCtRgb && !supportsRgb) {
				delete params.config.button_presets.button_doublepush.rgb;
			}

			const res: SettingRequest = await gen2Request(
				device,
				`${get_gen_2_component_from_category(device)}.SetConfig`,
				params,
			);

			const response = await onSettingsResponse(res, device);
			if (response.isok) {
				cacheBrightnessEnabled = enabledBrightness;
				cacheCtRgbEnabled = enabledCtRgb;
				cacheBrightness = brightness;
				if (supportsRgb) {
					chacheRgb = [...rgb];
				}
				if (supportsCt) {
					cacheCt = ct;
				}
			} else {
				enabledBrightness = cacheBrightnessEnabled;
				enabledCtRgb = cacheCtRgbEnabled;
				brightness = cacheBrightness;
				if (supportsRgb) {
					rgb = [...chacheRgb];
				}
				if (supportsCt) {
					ct = cacheCt;
				}
			}
			loading = false;
		}
	};

	const a11yProps = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness}`,
		}),
	};

	const a11yPropsRgb = {
		saveButtonAriaLabel: $format('label_save_color', { default: 'Save color' }),
		colorInputRAriaLabel: $format('label_color_r', { default: 'Color R' }),
		colorInputGAriaLabel: $format('label_color_g', { default: 'Color G' }),
		colorInputBAriaLabel: $format('label_color_b', { default: 'Color B' }),
	};
	const a11yPropsCt = {
		spinnerAriaLabel: $format('label_color_temperature_spinner', {
			default: 'Color temperature spinner',
		}),
		tempWheelAriaLabel: $format('label_color_temperature', { default: 'Color temperature' }),
		thumbAriaLabel: $format('label_color_temperature_thumb', { default: 'Color temperature thumb' }),
		incrementBtnAriaLabel: $format('label_color_temperature_increment_btn', {
			default: 'Color temperature increment btn',
		}),
		decrementBtnAriaLabel: $format('label_color_temperature_decrement_btn', {
			default: 'Color temperature decrement btn',
		}),
	};
</script>

<Text styleType="body-small" class="mb-m"
	>{$format('description_button_presets', {
		default: 'Set various properties on action. If not set it will use the current values.',
	})}</Text
>

<Alert
	class="mb-m"
	type="info"
	message={$format('alert_button_presets', {
		default:
			'Button presets requires input to be set to button mode and output type to be set to dimming.',
	})}
	cta={{
		title: $format('button_configure', {
			default: 'Configure',
		}),
	}}
	on:cta_click={() => navigateToSetting(device.id, 'settings', 'input_output')}
/>

<Toggle checked={enabledBrightness} on:click={toggleBrightness} size="m" class="mb-m" id="toggle-3skic60"
	>{$format('enable_brightness_preset', {
		default: 'Enable brigthness preset',
	})}</Toggle
>

{#if enabledBrightness}
	<Slider
		id="slider-dk1d4ui"
		{a11yProps}
		class="mb-s"
		value={brightness}
		on:value_change={(event) => {
			brightness = event.detail.value;
		}}
		disabled={!enabledBrightness}
		title="Brightness"
		type="simple"
		min={minBrightness}
		max={maxBrightness}
	/>
{/if}

<Toggle checked={enabledCtRgb} on:click={toggleCtRgb} size="m" class="mb-m" id="toggle-i8mmeqj"
	>{$format('enable_brightness_preset', {
		default: 'Enable brigthness preset',
	})}</Toggle
>

{#if enabledCtRgb}
	{#if supportsRgb}
		<ColorWheel
			id="color-wheel-rgbww"
			class="mb-xl"
			valuesType="input"
			a11yProps={a11yPropsRgb}
			{color}
			disabled={loading || !enabledCtRgb}
			hideSaveBtn
			on:color_change={changeColor}
		/>
	{:else if supportsCt}
		<TemperatureWheel
			class="mb-xl"
			a11yProps={a11yPropsCt}
			value={ct}
			min={minCt}
			max={maxCt}
			disabled={loading || !enabledCtRgb}
			on:value_change={(e) => changeCT(e.detail.value)}
		/>
	{/if}
{/if}

<Button size="m" width="full" {loading} on:click={sendReq}>{$format('button_save')}</Button>
